import { StorageDriver } from './StorageDriver';
import { BrowserIndexedDBDriver } from './BrowserIndexedDBDriver';
import { ElectronNodeFSDriver } from './ElectronNodeFSDriver';

class ServerMemoryDriver implements StorageDriver {
  async readFile(): Promise<string | null> {
    return null;
  }

  async writeFile(): Promise<void> {}

  async deleteFile(): Promise<void> {}

  async listFiles(): Promise<string[]> {
    return [];
  }

  async exists(): Promise<boolean> {
    return false;
  }

  async createDirectory(): Promise<void> {}
}

const hasDomWindow = typeof window !== 'undefined';
const canUseIndexedDb = typeof indexedDB !== 'undefined';

export class VFS {
  private driver: StorageDriver;
  private eventListeners: Map<string, ((event: any) => void)[]> = new Map();

  constructor(driver?: StorageDriver) {
    this.driver = driver || this.getStorageDriver();
  }

  on(event: string, listener: (event: any) => void) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(listener);
  }

  off(event: string, listener: (event: any) => void) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data: any) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((listener) => listener(data));
    }

    if (hasDomWindow) {
      window.dispatchEvent(
        new CustomEvent('fileSystemChanged', {
          detail: data,
        })
      );
    }
  }

  private getStorageDriver(): StorageDriver {
    if (!hasDomWindow || !canUseIndexedDb) {
      return new ServerMemoryDriver();
    }

    const isElectron = Boolean((window as any).electronAPI?.isElectron);

    if (isElectron) {
      console.log('VFS: Detected Electron environment - using native filesystem');
      try {
        return new ElectronNodeFSDriver();
      } catch (error) {
        console.error('Failed to initialize ElectronNodeFSDriver, falling back to IndexedDB:', error);
        return new BrowserIndexedDBDriver();
      }
    }

    console.log('VFS: Using browser IndexedDB storage');
    return new BrowserIndexedDBDriver();
  }

  async save(path: string, content: string, modified?: Date): Promise<void> {
    if (typeof (this.driver as any).writeFileWithMeta === 'function') {
      await (this.driver as any).writeFileWithMeta(path, content, modified);
    } else {
      await this.driver.writeFile(path, content);
    }

    this.emit('fileChanged', { path, type: 'save' });

    if (hasDomWindow) {
      try {
        const parent = path.includes('\\') ? path.substring(0, path.lastIndexOf('\\')) : path;
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: parent } }));
      } catch {}
    }
  }

  async load(path: string): Promise<string | null> {
    return await this.driver.readFile(path);
  }

  async delete(path: string): Promise<void> {
    await this.driver.deleteFile(path);
    this.emit('fileChanged', { path, type: 'delete' });
  }

  async list(path: string): Promise<string[]> {
    return await this.driver.listFiles(path);
  }

  async exists(path: string): Promise<boolean> {
    return await this.driver.exists(path);
  }

  async createDirectory(path: string, modified?: Date): Promise<void> {
    await (typeof (this.driver as any).createDirectoryWithMeta === 'function'
      ? (this.driver as any).createDirectoryWithMeta(path, modified)
      : this.driver.createDirectory(path));

    this.emit('fileChanged', { path, type: 'createDirectory' });

    if (hasDomWindow) {
      try {
        const parent = path.includes('\\') ? path.substring(0, path.lastIndexOf('\\')) : path;
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: parent } }));
      } catch {}
    }
  }

  async saveAppSettings(appId: string, settings: any): Promise<void> {
    if (this.driver instanceof BrowserIndexedDBDriver) {
      await this.driver.saveSettings(appId, settings);
    }
  }

  async loadAppSettings(appId: string): Promise<any> {
    if (this.driver instanceof BrowserIndexedDBDriver) {
      return await this.driver.loadSettings(appId);
    }
    return null;
  }

  async saveDesktopConfig(key: string, config: any): Promise<void> {
    if (this.driver instanceof BrowserIndexedDBDriver) {
      await this.driver.saveDesktopConfig(key, config);
    }
  }

  async loadDesktopConfig(key: string): Promise<any> {
    if (this.driver instanceof BrowserIndexedDBDriver) {
      return await this.driver.loadDesktopConfig(key);
    }
    return null;
  }

  async getAllFiles() {
    if (typeof (this.driver as any).getAllFiles === 'function') {
      return await (this.driver as any).getAllFiles();
    }
    return [];
  }

  async clearAll(): Promise<void> {
    if (typeof (this.driver as any).clearAll === 'function') {
      await (this.driver as any).clearAll();
    }
  }
}

export const globalVFS = new VFS();
